import React, { useState, useEffect } from 'react';

/**
 * Add Student to Attendance Report Modal Component
 * Allows selecting and adding students to an existing attendance session
 */
export default function AddStudentToReportModal({ 
  isOpen, 
  onClose, 
  onAddStudent, 
  availableStudents = [],
  isLoading = false 
}) {
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('PRESENT');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const statuses = [
    { value: 'PRESENT', label: 'Present' },
    { value: 'ABSENT', label: 'Absent' },
    { value: 'LATE', label: 'Late' },
    { value: 'EXCUSED', label: 'Excused' },
  ];

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedStudent('');
      setSelectedStatus('PRESENT');
      setSearchQuery('');
    }
  }, [isOpen]);

  // Filter students based on search query
  const filteredStudents = React.useMemo(() => {
    if (!searchQuery) return availableStudents;
    
    const query = searchQuery.toLowerCase();
    return availableStudents.filter(student => {
      const fullName = `${student.first_name || ''} ${student.last_name || ''}`.toLowerCase();
      const email = (student.email || '').toLowerCase();
      return fullName.includes(query) || email.includes(query);
    });
  }, [availableStudents, searchQuery]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedStudent) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const student = availableStudents.find(s => s.id === parseInt(selectedStudent));
      await onAddStudent(parseInt(selectedStudent), selectedStatus, student);
      onClose();
    } catch (error) {
      console.error('Failed to add student:', error);
      alert(error.message || 'Failed to add student to report');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleOverlayClick}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-800">
            Add Student to Report
          </h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* Search box */}
            <div>
              <label htmlFor="student-search" className="block text-sm font-medium text-slate-700 mb-1">
                Search Students
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
                  </svg>
                </span>
                <input
                  type="text"
                  id="student-search"
                  className="w-full border border-slate-300 rounded-lg pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Student selector */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select Student *
              </label>
              {isLoading ? (
                <div className="w-full px-3 py-8 border border-slate-300 rounded-lg bg-slate-50 flex flex-col items-center justify-center">
                  <svg className="w-8 h-8 animate-spin mb-2 text-blue-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-slate-500 text-sm">Loading students...</span>
                </div>
              ) : filteredStudents.length === 0 ? (
                <div className="w-full px-3 py-8 border border-slate-300 rounded-lg bg-slate-50">
                  <div className="text-center">
                    <svg className="mx-auto h-12 w-12 text-slate-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-slate-500">
                      {availableStudents.length === 0 
                        ? 'All students have been added to this report.' 
                        : 'No students found matching your search.'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  <div className="grid grid-cols-2 gap-3">
                    {filteredStudents.map((student) => (
                      <label
                        key={student.id}
                        className={`flex items-start p-4 cursor-pointer hover:bg-slate-50 transition-colors border border-slate-200 rounded-lg ${
                          selectedStudent === String(student.id) ? 'bg-blue-50 border-blue-300' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="student-selection"
                          value={student.id}
                          checked={selectedStudent === String(student.id)}
                          onChange={(e) => setSelectedStudent(e.target.value)}
                          className="h-4 w-4 mt-0.5 text-blue-600 focus:ring-blue-500 border-slate-300 flex-shrink-0"
                        />
                        <div className="ml-3 flex-1 min-w-0">
                          <div className="font-medium text-slate-900 truncate">
                            {student.first_name} {student.last_name}
                          </div>
                          {student.email && (
                            <div className="text-sm text-slate-500 truncate">
                              {student.email}
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Status selector */}
            <div>
              <label htmlFor="status-select" className="block text-sm font-medium text-slate-700 mb-1">
                Status *
              </label>
              <select
                id="status-select"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                required
              >
                {statuses.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !selectedStudent || isLoading || filteredStudents.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Adding...
                </span>
              ) : (
                'Add Student'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
